import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function RefundChoice({ value, current, onChange, disabled, title, sub, children }) {
  const active = current === value;
  return (
    <label className={`block rounded-xl border px-4 py-3 transition-colors ${disabled ? "opacity-50 cursor-not-allowed bg-aviana-surface/40" : "cursor-pointer"} ${active ? "border-aviana-plum bg-white" : "border-aviana-border bg-white/60"}`}
      data-testid={`cancel-refund-option-${value}`}>
      <div className="flex items-start gap-3">
        <input type="radio" name="refund_mode" checked={active} disabled={disabled} onChange={() => onChange(value)} className="accent-aviana-plum mt-1" data-testid={`cancel-refund-radio-${value}`} />
        <div className="flex-1">
          <p className="text-sm text-aviana-ink font-medium">{title}</p>
          {sub && <p className="text-[12px] text-aviana-muted mt-0.5 leading-relaxed">{sub}</p>}
          {active && children}
        </div>
      </div>
    </label>
  );
}

function AmountInput({ value, onChange, max, testid }) {
  return (
    <div className="mt-3 flex items-center gap-2">
      <span className="text-sm text-aviana-muted">$</span>
      <input type="number" min="0" step="0.01" max={max} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-32 bg-aviana-cream border border-aviana-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-aviana-plum" data-testid={testid} />
      {max != null && <span className="text-[11px] text-aviana-muted">max {money(max)}</span>}
    </div>
  );
}

function refundSummary(info, mode, amount) {
  if (mode === "none") return "No refund";
  if (mode === "card") return info.hold_only ? "Release the held card (nothing charged or refunded)" : `Refund ${money(amount)} to card on file (automatic)`;
  return `Marked as refunded via Venmo/Zelle${Number(amount) > 0 ? ` · ${money(amount)}` : ""} (no automatic charge)`;
}

function ConfirmStep({ info, mode, amount, sendEmail, note }) {
  const rows = [
    ["Guest", `${info.guest_name}${info.guest_email ? ` · ${info.guest_email}` : ""}`],
    ["Event", `${info.offering_title}${info.date_label ? ` · ${info.date_label}` : ""}`],
    ["Refund", refundSummary(info, mode, amount)],
    ["Email", sendEmail ? "Send cancellation confirmation to guest" : "No email will be sent"],
  ];
  if (sendEmail && note.trim()) rows.push(["Personal note", note.trim()]);
  return (
    <div className="space-y-4" data-testid="cancel-confirm-step">
      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800">
        <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
        <p>Please review before confirming. The registration will be cancelled and the seat released back to the event.</p>
      </div>
      <dl className="bg-white border border-aviana-border rounded-xl divide-y divide-aviana-border/60">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-4 px-4 py-3 text-sm">
            <dt className="w-28 flex-shrink-0 text-[10px] tracking-[0.18em] uppercase text-aviana-plum pt-0.5">{k}</dt>
            <dd className="text-aviana-ink whitespace-pre-line" data-testid={`cancel-confirm-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export const CancelRegistrationModal = ({ source, id, onClose, onDone }) => {
  const [info, setInfo] = useState(null);
  const [step, setStep] = useState("options");
  const [mode, setMode] = useState("none");
  const [cardAmount, setCardAmount] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/registrations/${source}/${id}/cancel-info`)
      .then(({ data }) => { setInfo(data); setCardAmount(String(data.card_refundable_usd || "")); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail) || "Could not load registration"); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, id]);

  const amount = mode === "card" ? cardAmount : manualAmount;
  const cardInvalid = mode === "card" && info && !info.hold_only && (!(Number(cardAmount) > 0) || Number(cardAmount) > info.card_refundable_usd);

  const confirm = async () => {
    setBusy(true);
    try {
      const body = { refund_mode: mode, send_email: sendEmail, note };
      if (mode === "card" && !info.hold_only) body.refund_amount_usd = Number(cardAmount);
      if (mode === "manual") body.refund_amount_usd = Number(manualAmount) || 0;
      const { data } = await api.post(`/admin/registrations/${info.source}/${info.id}/cancel`, body);
      toast.success(`Registration cancelled${data.refunded_usd ? ` · ${money(data.refunded_usd)} refunded` : ""}${data.email_sent ? " · email sent" : ""}`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] text-left whitespace-normal bg-aviana-ink/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cancel-registration-modal">
      <div className="bg-aviana-cream rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl">
        <header className="px-6 py-5 border-b border-aviana-border flex items-start justify-between gap-4">
          <div>
            <p className="overline-plum mb-1">{step === "confirm" ? "Confirm cancellation" : "Cancel registration"}</p>
            <h2 className="font-display text-2xl text-aviana-ink">{info ? info.guest_name : "Loading…"}</h2>
            {info && <p className="text-sm text-aviana-muted mt-0.5">{info.offering_title}{info.date_label ? ` · ${info.date_label}` : ""}</p>}
          </div>
          <button onClick={onClose} className="text-aviana-muted hover:text-aviana-ink transition-colors" data-testid="cancel-modal-close"><X size={20} /></button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {!info && <div className="py-10 text-center text-aviana-muted"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>}
          {info && step === "options" && (
            <div className="space-y-5" data-testid="cancel-options-step">
              <div className="space-y-2.5">
                <p className="text-[10px] tracking-[0.2em] uppercase text-aviana-plum">Refund</p>
                <RefundChoice value="none" current={mode} onChange={setMode} title="No refund" />
                <RefundChoice value="card" current={mode} onChange={setMode} disabled={!info.card_available}
                  title={info.hold_only ? "Release card on file (automatic)" : "Refund to card on file (automatic)"}
                  sub={info.card_note || `Paid by card: ${money(info.card_paid_usd)} · refundable ${money(info.card_refundable_usd)}`}>
                  {!info.hold_only && <AmountInput value={cardAmount} onChange={setCardAmount} max={info.card_refundable_usd} testid="cancel-card-amount" />}
                </RefundChoice>
                <RefundChoice value="manual" current={mode} onChange={setMode} title="Marked as refunded via Venmo/Zelle"
                  sub="Manual — you send the money yourself; nothing is charged or refunded automatically.">
                  <AmountInput value={manualAmount} onChange={setManualAmount} testid="cancel-manual-amount" />
                </RefundChoice>
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="cancel-send-email-label">
                <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-aviana-plum mt-1" data-testid="cancel-send-email" />
                <span className="text-sm text-aviana-ink">Send cancellation confirmation email to guest</span>
              </label>
              {sendEmail && (
                <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional personal note included in the email"
                  className="w-full bg-white border border-aviana-border rounded-xl px-4 py-3 text-sm outline-none focus:border-aviana-plum" data-testid="cancel-note" />
              )}
            </div>
          )}
          {info && step === "confirm" && <ConfirmStep info={info} mode={mode} amount={amount} sendEmail={sendEmail} note={note} />}
        </div>
        <footer className="px-6 py-4 border-t border-aviana-border flex items-center justify-end gap-3">
          {step === "options" ? (
            <>
              <button onClick={onClose} className="btn-secondary !py-2 !px-5 !text-[11px]" data-testid="cancel-modal-dismiss">Keep registration</button>
              <button onClick={() => setStep("confirm")} disabled={!info || cardInvalid}
                className="!py-2.5 !px-6 !text-[11px] rounded-full bg-red-600 text-white tracking-[0.18em] uppercase hover:bg-red-700 disabled:opacity-50 transition-colors" data-testid="cancel-continue">
                Continue
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep("options")} disabled={busy} className="btn-secondary !py-2 !px-5 !text-[11px]" data-testid="cancel-confirm-back">Cancel</button>
              <button onClick={confirm} disabled={busy}
                className="!py-2.5 !px-6 !text-[11px] rounded-full bg-red-600 text-white tracking-[0.18em] uppercase hover:bg-red-700 disabled:opacity-50 transition-colors" data-testid="cancel-confirm-submit">
                {busy ? "Cancelling…" : "Confirm"}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
};

export const CancelRegistrationButton = ({ source, id, onDone, className = "", label = "Cancel registration" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`cancel-registration-${source}-${id}`}
        className={className || "px-3 py-1.5 rounded-md border border-red-200 text-red-600 text-[11px] hover:bg-red-500 hover:text-white transition-colors"}>
        {label}
      </button>
      {open && <CancelRegistrationModal source={source} id={id} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }} />}
    </>
  );
};

export default CancelRegistrationModal;
